import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { PopoutDialog } from "@/components/ui/PopoutDialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import AiDisclaimer from "@/components/AiDisclaimer";
import {
  LifeBuoy, Send, ArrowLeft, BookOpen, ExternalLink, AlertTriangle, ArrowRight,
  Sparkles, ShieldAlert, Loader2, ChevronRight,
} from "lucide-react";

const C = { navy: "#1C4358", red: "#9C1E22", green: "#2F7D5A", amber: "#B7791F", charcoal: "#252221", slate: "#4B5563", line: "#E6EDF2" };

const HelpContext = createContext({ openHelp: () => {}, closeHelp: () => {}, suggestionSummary: null });
export const useHelp = () => useContext(HelpContext);

export function HelpProvider({ children, external = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isExternal = external || user?.role === "partner_vendor";
  const [open, setOpen] = useState(false);
  const [overrideRoute, setOverrideRoute] = useState(null);
  const [settings, setSettings] = useState(null);
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(false);
  const [question, setQuestion] = useState("");
  const [asking, setAsking] = useState(false);
  const [answer, setAnswer] = useState(null);
  const [activeSop, setActiveSop] = useState(null);
  const [summary, setSummary] = useState(null);
  const [sessionId] = useState(() => Math.random().toString(36).slice(2));

  const route = overrideRoute || location.pathname;

  const loadSummary = useCallback(() => {
    if (!user?.is_admin) { setSummary(null); return; }
    api.get("/help-assistant/suggestions/summary").then(({ data }) => setSummary(data)).catch(() => setSummary(null));
  }, [user]);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  const openHelp = useCallback((r = null) => { setOverrideRoute(r); setActiveSop(null); setAnswer(null); setOpen(true); }, []);
  const closeHelp = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    if (!settings) api.get("/help-assistant/settings").then(({ data }) => setSettings(data)).catch(() => setSettings({ assistant_enabled: false }));
    loadSummary();
    setLoading(true);
    api.get("/sops/contextual-help", { params: { route } })
      .then(({ data }) => setCtx(data))
      .catch(() => setCtx({ sops: [], page: null }))
      .finally(() => setLoading(false));
  }, [open, route]); // eslint-disable-line

  const ask = async () => {
    if (!question.trim()) return;
    setAsking(true); setAnswer(null);
    try {
      const { data } = await api.post("/help-assistant/ask", { question: question.trim(), route, session_id: sessionId });
      setAnswer(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setAsking(false); }
  };

  const openSop = async (id) => {
    setActiveSop("loading");
    try { const { data } = await api.get(`/sops/${id}`); setActiveSop(data); }
    catch (e) { toast.error(errMsg(e)); setActiveSop(null); }
  };

  const requestGuide = async () => {
    if (!question.trim()) return;
    try {
      await api.post("/help-assistant/suggestions", { title: question.trim(), route, reason: "Requested from the help drawer" });
      toast.success("Thanks — your request was sent to the admins to consider.");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const goFullPage = (id) => { setOpen(false); navigate(`/sops/${id}`); };

  return (
    <HelpContext.Provider value={{ openHelp, closeHelp, suggestionSummary: summary }}>
      {children}
      <PopoutDialog open={open} onOpenChange={setOpen} mode="float" allowFloat={false}
        title="Help" subtitle={ctx?.page?.title ? `· ${ctx.page.title}` : "Guides for this page"}
        icon={LifeBuoy} testid="help-drawer" maxWidthClass="max-w-[440px]">
        <div className="space-y-4">
            {activeSop ? (
              <SopInline sop={activeSop} onBack={() => setActiveSop(null)} onFull={isExternal ? null : goFullPage} />
            ) : (
              <>
                {summary && summary.total_to_review > 0 && (
                  <div className="rounded-lg p-2.5 flex items-center justify-between gap-2" style={{ background: "rgba(183,121,31,0.10)" }} data-testid="help-suggestions-line">
                    <div className="text-[12.5px]" style={{ color: "#92400E" }}>
                      {summary.pending_suggestions > 0 && <span>{summary.pending_suggestions} SOP suggestion{summary.pending_suggestions > 1 ? "s" : ""} need review</span>}
                      {summary.pending_suggestions > 0 && summary.ai_drafts > 0 && " · "}
                      {summary.ai_drafts > 0 && <span>{summary.ai_drafts} AI draft{summary.ai_drafts > 1 ? "s" : ""}</span>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button data-testid="help-review-suggestions" onClick={() => { setOpen(false); navigate("/sops/suggestions"); }} className="text-[12px] font-semibold" style={{ color: C.navy }}>Review →</button>
                      {summary.ai_drafts > 0 && <button data-testid="help-review-drafts" onClick={() => { setOpen(false); navigate("/sops/review"); }} className="text-[12px] font-semibold" style={{ color: C.navy }}>Drafts</button>}
                    </div>
                  </div>
                )}
                {settings?.assistant_enabled !== false && (
                  <div data-testid="help-ask-block">
                    <div className="text-[12px] font-semibold mb-1.5 flex items-center gap-1" style={{ color: C.navy }}>
                      <Sparkles size={13} /> Ask the assistant
                    </div>
                    <Textarea data-testid="help-ask-input" value={question} onChange={(e) => setQuestion(e.target.value)}
                      rows={2} placeholder={`How do I…${ctx?.page ? ` (on ${ctx.page.title})` : ""}?`}
                      className="text-[13px]" onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) ask(); }} />
                    <Button data-testid="help-ask-submit" onClick={ask} disabled={asking || !question.trim()}
                      className="mt-2 h-8 w-full gap-1 bg-[#1C4358] hover:bg-[#16374a] text-white text-[13px]">
                      {asking ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Ask
                    </Button>
                  </div>
                )}

                {answer && (
                  <div className="rounded-lg border p-3" style={{ borderColor: C.line }} data-testid="help-answer">
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                        style={answer.ai_used ? { background: "rgba(28,67,88,0.1)", color: C.navy } : { background: "#EEF2F5", color: C.slate }}>
                        {answer.ai_used ? "AI answer" : "Offline guide"}
                      </span>
                    </div>
                    {answer.ai_used && <AiDisclaimer compact className="mb-2" />}
                    {answer.needs_professional && (
                      <div className="rounded-md p-2 mb-2 text-[12px] flex items-start gap-1.5" style={{ background: "#FEF3C7", color: "#92400E" }} data-testid="help-needs-professional">
                        <ShieldAlert size={14} className="mt-0.5 shrink-0" /> This may need review by the appropriate professional.
                      </div>
                    )}
                    <p className="text-[13.5px]" style={{ color: C.charcoal }}>{answer.answer}</p>
                    {answer.steps?.length > 0 && (
                      <ol className="mt-2 space-y-1.5">
                        {answer.steps.map((s, i) => (
                          <li key={i} className="flex gap-2 text-[13px]" style={{ color: C.slate }}>
                            <span className="shrink-0 w-4 h-4 rounded-full text-white text-[10px] flex items-center justify-center mt-0.5" style={{ background: C.navy }}>{i + 1}</span>{s}
                          </li>
                        ))}
                      </ol>
                    )}
                    {answer.warnings?.length > 0 && (
                      <div className="mt-2 text-[12px]" style={{ color: "#78350F" }}>
                        {answer.warnings.map((w, i) => <div key={i} className="flex gap-1"><AlertTriangle size={12} className="mt-0.5 shrink-0" /> {w}</div>)}
                      </div>
                    )}
                    {answer.next_steps && <p className="mt-2 text-[12.5px]" style={{ color: C.green }}><ArrowRight size={12} className="inline" /> {answer.next_steps}</p>}
                    {answer.related_sops?.length > 0 && (
                      <div className="mt-3 pt-2 border-t" style={{ borderColor: C.line }}>
                        <div className="text-[11px] font-semibold mb-1" style={{ color: C.slate }}>Related guides</div>
                        {answer.related_sops.map((s) => (
                          <button key={s.id} onClick={() => openSop(s.id)} className="block text-left text-[12.5px] py-0.5 hover:underline" style={{ color: C.navy }}>
                            <ChevronRight size={11} className="inline" /> {s.title}
                          </button>
                        ))}
                      </div>
                    )}
                    {answer.disclaimer && <p className="mt-2 text-[10.5px] italic" style={{ color: C.slate }}>{answer.disclaimer}</p>}
                    {answer.answered === false && (
                      <button onClick={requestGuide} data-testid="help-request-guide" className="mt-2 text-[12px] font-medium" style={{ color: C.navy }}>
                        Can't find it? Request a guide →
                      </button>
                    )}
                  </div>
                )}

                <div data-testid="help-guides">
                  <div className="text-[12px] font-semibold mb-2 flex items-center gap-1" style={{ color: C.navy }}>
                    <BookOpen size={13} /> {ctx?.page ? "Guides for this page" : "Guides for you"}
                  </div>
                  {loading && <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-12" />)}</div>}
                  {!loading && (ctx?.sops || []).length === 0 && (
                    <p className="text-[12.5px]" style={{ color: C.slate }}>No guides for this screen yet.</p>
                  )}
                  <div className="space-y-1.5">
                    {(ctx?.sops || []).map((s) => (
                      <button key={s.id} data-testid={`help-guide-${s.id}`} onClick={() => openSop(s.id)}
                        className="w-full text-left rounded-lg border p-2.5 hover:shadow-sm transition-shadow" style={{ borderColor: C.line }}>
                        <div className="text-[13px] font-medium" style={{ color: C.navy }}>{s.title}</div>
                        {s.summary && <div className="text-[11.5px] line-clamp-1" style={{ color: C.slate }}>{s.summary}</div>}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

          {!isExternal && (
            <div className="pt-3 mt-1 border-t" style={{ borderColor: C.line }}>
              <button onClick={() => { setOpen(false); navigate("/sops"); }} className="text-[12.5px] flex items-center gap-1" style={{ color: C.navy }} data-testid="help-open-library">
                <ExternalLink size={13} /> Open full SOP Library
              </button>
            </div>
          )}
        </div>
      </PopoutDialog>
    </HelpContext.Provider>
  );
}

function SopInline({ sop, onBack, onFull }) {
  if (sop === "loading") return <div className="space-y-2"><Skeleton className="h-6 w-2/3" /><Skeleton className="h-32" /></div>;
  return (
    <div data-testid="help-sop-inline">
      <button onClick={onBack} className="text-[12.5px] flex items-center gap-1 mb-2" style={{ color: C.slate }}><ArrowLeft size={13} /> Back</button>
      <h3 className="font-display font-bold text-[16px]" style={{ color: C.charcoal }}>{sop.title}</h3>
      {sop.summary && <p className="text-[13px] mt-1" style={{ color: C.slate }}>{sop.summary}</p>}
      {sop.role_requirements && <p className="text-[12px] mt-2 rounded-md p-2" style={{ background: "rgba(28,67,88,0.05)", color: C.navy }}>👤 {sop.role_requirements}</p>}
      {(sop.steps || []).length > 0 && (
        <ol className="mt-3 space-y-2">
          {sop.steps.map((st, i) => (
            <li key={i} className="flex gap-2">
              <span className="shrink-0 w-5 h-5 rounded-full text-white text-[11px] font-bold flex items-center justify-center" style={{ background: C.navy }}>{i + 1}</span>
              <div><div className="text-[13px] font-semibold" style={{ color: C.charcoal }}>{st.title}</div><div className="text-[12.5px]" style={{ color: C.slate }}>{st.detail}</div></div>
            </li>
          ))}
        </ol>
      )}
      {(sop.warnings || []).length > 0 && (
        <div className="mt-3 rounded-md p-2 text-[12px]" style={{ background: "#FEF3C7", color: "#78350F" }}>
          {sop.warnings.map((w, i) => <div key={i} className="flex gap-1"><AlertTriangle size={12} className="mt-0.5 shrink-0" /> {w}</div>)}
        </div>
      )}
      {sop.next_steps && <p className="mt-3 text-[12.5px]" style={{ color: C.green }}><ArrowRight size={12} className="inline" /> {sop.next_steps}</p>}
      {onFull && <button onClick={() => onFull(sop.id)} className="mt-4 text-[12.5px] flex items-center gap-1" style={{ color: C.navy }}><ExternalLink size={12} /> Open full guide</button>}
    </div>
  );
}
